import React from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { CheckCircle2, XCircle, Clock, Save, CalendarCheck } from "lucide-react";
import { SchoolCheckinSettings } from "../components/SchoolCheckinSettings";

const STATUSES = [
    { key: "present", label: "Present", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", active: "bg-emerald-500 text-white border-emerald-500", icon: CheckCircle2 },
    { key: "late", label: "Late", cls: "bg-amber-100 text-amber-800 border-amber-200", active: "bg-amber-500 text-white border-amber-500", icon: Clock },
    { key: "absent", label: "Absent", cls: "bg-rose-100 text-rose-800 border-rose-200", active: "bg-rose-500 text-white border-rose-500", icon: XCircle },
];

function todayIso() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function AdminTeacherAttendance() {
    const [date, setDate] = React.useState(todayIso());
    const [rows, setRows] = React.useState([]);
    const [notes, setNotes] = React.useState({});
    const [mins, setMins] = React.useState({}); // { teacher_id: arrival "HH:MM" }
    const [secs, setSecs] = React.useState({}); // { teacher_id: sections } for teacher/volunteers
    const [dirty, setDirty] = React.useState({});
    const [saving, setSaving] = React.useState(false);

    const load = React.useCallback(() => {
        api.get("/admin/teacher-attendance", { params: { date } }).then(r => {
            setRows(r.data.rows);
            const n = {}, m = {}, sc = {};
            r.data.rows.forEach(row => {
                n[row.teacher_id] = row.note || "";
                m[row.teacher_id] = row.arrival_time || "";
                sc[row.teacher_id] = row.sections ?? 1;
            });
            setNotes(n);
            setMins(m);
            setSecs(sc);
            setDirty({});
        });
    }, [date]);
    React.useEffect(() => { load(); }, [load]);

    function setStatus(tid, status) {
        setRows(rs => rs.map(r => r.teacher_id === tid ? { ...r, status } : r));
        setDirty(d => ({ ...d, [tid]: true }));
    }
    function setNote(tid, note) {
        setNotes(n => ({ ...n, [tid]: note }));
        setDirty(d => ({ ...d, [tid]: true }));
    }
    function setMinutes(tid, v) {
        // keep as string so user can clear it; convert on save
        setMins(m => ({ ...m, [tid]: v }));
        setDirty(d => ({ ...d, [tid]: true }));
    }

    async function saveAll() {
        const entries = rows
            .filter(r => dirty[r.teacher_id] && r.status)
            .map(r => {
                const entry = { teacher_id: r.teacher_id, status: r.status, note: notes[r.teacher_id] || null };
                if ((r.status === "late" || r.status === "present") && mins[r.teacher_id]) {
                    entry.arrival_time = mins[r.teacher_id];
                }
                if (r.staff_type === "teacher_volunteer" && r.status !== "absent") {
                    const s = parseInt(secs[r.teacher_id], 10);
                    entry.sections = Number.isFinite(s) && s >= 0 ? s : 1;
                }
                return entry;
            });
        if (entries.length === 0) return toast.info("No changes to save");
        setSaving(true);
        try {
            await api.post("/admin/teacher-attendance", { date, entries });
            toast.success(`Saved ${entries.length} teacher${entries.length === 1 ? "" : "s"}`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    }

    const selfReports = rows.filter(r => r.self_reported);
    const dirtyCount = Object.values(dirty).filter(Boolean).length;

    return (
        <div>
            <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
                <div>
                    <div className="overline text-brand-stone">Admin</div>
                    <h1 className="font-serif text-3xl">Teacher attendance</h1>
                </div>
                <div className="flex items-end gap-3">
                    <div>
                        <div className="text-[11px] text-brand-stone mb-1">Session date</div>
                        <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="teacher-attendance-date" className="w-48" />
                    </div>
                    <Button
                        onClick={saveAll}
                        disabled={saving || dirtyCount === 0}
                        data-testid="save-teacher-attendance-btn"
                        className="bg-primary hover:bg-primary/90 gap-2"
                    >
                        <Save className="w-4 h-4" /> Save {dirtyCount > 0 ? `(${dirtyCount})` : ""}
                    </Button>
                </div>
            </div>

            <SchoolCheckinSettings />
            {selfReports.length > 0 && (
                <Card className="mb-6 border-amber-200 bg-amber-50/60">
                    <CardContent className="p-5">
                        <div className="flex items-center gap-2 mb-2">
                            <CalendarCheck className="w-4 h-4 text-amber-700" />
                            <div className="font-dash font-semibold text-amber-900">Self-reported call-outs for {date}</div>
                        </div>
                        <div className="space-y-1.5">
                            {selfReports.map(r => (
                                <div key={r.teacher_id} className="text-sm flex items-center gap-2 flex-wrap">
                                    <Badge className={r.status === "late" ? "bg-amber-100 text-amber-800 border-0" : "bg-rose-100 text-rose-800 border-0"}>{r.status}</Badge>
                                    <b>{r.teacher_name}</b>
                                    {r.status === "late" && r.minutes_late != null && (
                                        <span className="text-amber-900 font-medium">· {r.minutes_late} min late</span>
                                    )}
                                    {r.note && <span className="text-brand-stone italic">— "{r.note}"</span>}
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="space-y-2">
                {rows.map(r => (
                    <Card key={r.teacher_id} data-testid={`teacher-att-row-${r.teacher_id}`}>
                        <CardContent className="p-4 flex items-center gap-4 flex-wrap">
                            <div className="min-w-0 flex-1">
                                <div className="font-medium flex items-center gap-2">
                                    {r.teacher_name}
                                    {r.staff_type === "teacher_volunteer" && <Badge className="bg-blue-100 text-blue-800 border-0 text-[10px]">Teacher/Volunteer</Badge>}
                                </div>
                                <div className="text-xs text-brand-stone">{r.email}</div>
                                {r.marked_by_role && r.status && (
                                    <div className="text-[11px] text-brand-stone italic mt-0.5">
                                        {r.self_reported ? "Self-reported" : `Marked by ${r.marked_by_name}`}
                                    </div>
                                )}
                            </div>
                            <div className="flex gap-2 items-center flex-wrap">
                                {STATUSES.map(s => {
                                    const active = r.status === s.key;
                                    const Icon = s.icon;
                                    return (
                                        <button
                                            key={s.key}
                                            onClick={() => setStatus(r.teacher_id, s.key)}
                                            data-testid={`mark-${s.key}-${r.teacher_id}`}
                                            className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-md border text-xs font-medium transition ${active ? s.active : s.cls + " hover:opacity-90"}`}
                                        >
                                            <Icon className="w-3.5 h-3.5" /> {s.label}
                                        </button>
                                    );
                                })}
                                {(r.status === "late" || r.status === "present") && (
                                    <div className="inline-flex items-center gap-1.5 pl-1">
                                        <span className="text-[11px] text-brand-stone">arrived</span>
                                        <Input
                                            type="time"
                                            value={mins[r.teacher_id] ?? ""}
                                            onChange={e => setMinutes(r.teacher_id, e.target.value)}
                                            data-testid={`arrival-time-${r.teacher_id}`}
                                            className="h-8 w-28 text-xs"
                                        />
                                    </div>
                                )}
                                {r.staff_type === "teacher_volunteer" && (r.status === "present" || r.status === "late") && (
                                    <div className="inline-flex items-center gap-1.5 pl-1">
                                        <Input
                                            type="number"
                                            min={0}
                                            max={10}
                                            value={secs[r.teacher_id] ?? 1}
                                            onChange={e => { setSecs(s => ({ ...s, [r.teacher_id]: e.target.value })); setDirty(d => ({ ...d, [r.teacher_id]: true })); }}
                                            data-testid={`sections-${r.teacher_id}`}
                                            className="h-8 w-16 text-xs"
                                        />
                                        <span className="text-[11px] text-brand-stone">sections</span>
                                    </div>
                                )}
                            </div>
                            <Input
                                placeholder="Note (optional)"
                                value={notes[r.teacher_id] || ""}
                                onChange={e => setNote(r.teacher_id, e.target.value)}
                                data-testid={`teacher-att-note-${r.teacher_id}`}
                                className="w-full md:w-56"
                            />
                        </CardContent>
                    </Card>
                ))}
                {rows.length === 0 && (
                    <div className="text-center py-16 text-brand-stone">No active teachers.</div>
                )}
            </div>
        </div>
    );
}
